import { RiArrowLeftRightLine } from "@remixicon/react";
import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TicketItem, useRegisterAction, useTicketState } from "../../../contexts/ticket-state";
import {
    ActionContext,
    ActionHandler,
    ActionType,
    HANDLER_INTERFACE,
    HandlerButton,
} from "../../../contexts/ticket-state/action-registry";
import useChangingCallback from "../../../use-changing-callback";
import ColumnPicker from "../../pickers/ticket-states/ColumnPicker";
import Dropdown from "../../theme/dropdown/Dropdown";
import Styles from "./move.module.scss";

function Button({
    button, onSelected, onClick,
}: {
    button: HandlerButton;
    onSelected(ids: string): void;
    onClick: () => void;
}) {
    const [shouldShow, setShouldShow] = useState(false);

    const doToggle = (isNextShow: boolean) => {
        setShouldShow(isNextShow);
        if (isNextShow) {
            onClick();
        }
    };

    return (
        <Dropdown show={shouldShow} onToggle={doToggle} className="action">
            <Dropdown.Toggle
                size="small"
                onClick={() => {
                    setShouldShow(!shouldShow);
                    if (!shouldShow) {
                        onClick();
                    }
                }}
                variant={button.variant}
                prefixIcon={button.icon}
            >
                {button.text}
            </Dropdown.Toggle>
            {createPortal(
                <Dropdown.Menu className={Styles.container}>
                    <span className={Styles.label}>Move to:</span>
                    <ColumnPicker onSelected={onSelected} className={Styles.picker} />
                </Dropdown.Menu>,
                document.body,
            )}
        </Dropdown>
    );
}

export default function Move() {
    const ticketState = useTicketState();

    const actionHandler = useRef<ActionHandler>();

    const createBulkActionButton = useChangingCallback(
        (items: TicketItem[], context: ActionContext) => (
            context === ActionContext.BULK_ACTION ? {
                text: "Move",
                icon: <RiArrowLeftRightLine />,
                variant: "ghost-gray",
                render: props => (
                    <Button
                        onSelected={targetId => {
                            const column = ticketState.dashboardView?.getColumn(targetId);

                            const tickets = actionHandler?.current?.items;
                            if (column && tickets) {
                                ticketState
                                    .dashboardView?.moveTickets(tickets, column, ActionContext.TICKET_ACTION, () => {
                                        actionHandler?.current?.done();
                                    });
                            }
                        }}
                        {...props}
                    />
                ),
            } as HandlerButton : undefined),
        [ticketState],
    );

    useRegisterAction({
        createBulkActionButton,
        handleBulkAction(handler) {
            if (handler.context === ActionContext.TICKET_ACTION) {
                handler.items.forEach(item => item.closeIfOpen());
            }

            actionHandler.current = handler;
        },
    }, ActionType.MOVE);

    return null;
}

HANDLER_INTERFACE.register(Move);
